/* eslint-disable max-lines */
import cx from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";
import { Badge, ButtonType, HeroButton, Icon, IconSize, InlineTabs } from "@gaming/xds-react-js";
import {
  CheckInCircle,
  CircleStop,
  Clock,
  Settings,
  StatusExclamation,
} from "@gaming/xds-react-js-icons";

import { Modal } from "../../components/Modal/Modal";
import { SelectDropdown } from "../../components/SelectDropdown";
import { LoadingSpinner } from "../../components/Spinner";
import { PageType, redirectToSignIn } from "../../constants";
import { getImage, ImageType } from "../../helpers/imageHelper";
import { useMinPageHeight } from "../../helpers/windowHelpers";
import {
  PartnerCenterSeller,
  TicketingAccessMode,
  TicketPaginationParams,
  TicketSeverityLabel,
  TicketStatusLabel,
} from "../../models/ticketing";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getTicketingAccessMode,
  getTicketingCategories,
  getTicketingIsNewContact,
  getTicketingIsPaginatedFetching,
  getTicketingIsTicketsFetched,
  getTicketingIsVerified,
  getTicketingIsVerifyChecked,
  getTicketingPagination,
  getTicketingSelectedAccessId,
  getTicketingSellers,
  getTicketingStatusCounts,
  getTicketingSubCategories,
  getTicketingSubmitSuccess,
  getTicketingTeams,
  getTicketingTickets,
  getTicketingTranslationMap,
} from "../../redux/selectors/ticketingSelectors";
import { AccessDeniedPage } from "../AccessDeniedPage/AccessDeniedPage";
import { NotificationEmailPrompt } from "./NotificationEmailPrompt";
import { NotificationEmailSection } from "./NotificationEmailSection";
import { FilterPanelValues, TicketingFilterPanel } from "./TicketingFilterPanel";
import * as styles from "./TicketingPage.module.css";
import { useTicketingStrings } from "./ticketingStrings";
import { translateSeverityLabel, translateStatusLabel } from "./translateValue";

const severityLevels = [
  TicketSeverityLabel.Low,
  TicketSeverityLabel.Medium,
  TicketSeverityLabel.High,
  TicketSeverityLabel.Critical,
];

/** Map client sort key to CRM OData field name */
const sortKeyToCrmField: Record<SortKey, string | undefined> = {
  ticketNumber: "ticket_ticketnumber",
  title: "ticket_title",
  teamName: "_ticket_team_value",
  severity: "ticket_severity",
  status: "statuscode",
  createdOn: "createdon",
  submittedBy: undefined, // not sortable server-side
  statusDate: undefined, // not sortable server-side
};

/** Map severity label to CRM enum value */
const severityLabelToValue: Record<string, number> = {
  [TicketSeverityLabel.Low]: 751650000,
  [TicketSeverityLabel.Medium]: 751650001,
  [TicketSeverityLabel.High]: 751650002,
  [TicketSeverityLabel.Critical]: 751650003,
};

/** Map status label to CRM statuscode values (Closed includes both closed + inactiveClosed) */
const statusLabelToValues: Record<string, number[]> = {
  [TicketStatusLabel.Proposed]: [751650001],
  [TicketStatusLabel.Active]: [1],
  [TicketStatusLabel.Resolved]: [751650002],
  [TicketStatusLabel.Closed]: [751650003, 751650004],
};

type SortKey =
  | "ticketNumber"
  | "title"
  | "teamName"
  | "severity"
  | "status"
  | "submittedBy"
  | "createdOn"
  | "statusDate";
type SortDir = "asc" | "desc";

const TicketingPage = () => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isSignedIn = useSelector(getIsUserSignedIn);
  const isVerified = useSelector(getTicketingIsVerified);
  const isVerifyChecked = useSelector(getTicketingIsVerifyChecked);
  const tickets = useSelector(getTicketingTickets);
  const isTicketsFetched = useSelector(getTicketingIsTicketsFetched);
  const accessMode = useSelector(getTicketingAccessMode);
  const sellers = useSelector(getTicketingSellers);
  const selectedAccessId = useSelector(getTicketingSelectedAccessId);
  const isNewContact = useSelector(getTicketingIsNewContact);
  const translationMap = useSelector(getTicketingTranslationMap);
  const pagination = useSelector(getTicketingPagination);
  const isPaginatedFetching = useSelector(getTicketingIsPaginatedFetching);
  const submitSuccess = useSelector(getTicketingSubmitSuccess);
  const statusCountsFromRedux = useSelector(getTicketingStatusCounts);
  const teams = useSelector(getTicketingTeams);
  const categories = useSelector(getTicketingCategories);
  const subCategories = useSelector(getTicketingSubCategories);
  const minPageHeight = useMinPageHeight();

  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, _setSortKey] = useState<SortKey>("createdOn");
  const [sortDir, _setSortDir] = useState<SortDir>("desc");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");
  const [tagsFilter, setTagsFilter] = useState("");
  const [isEmailPromptOpen, setIsEmailPromptOpen] = useState(false);
  const [isEmailVerifiedSuccess, setIsEmailVerifiedSuccess] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
      const timer = setTimeout(() => {
        dispatch(ticketingActions.setPaginationParams({ page: 1 }));
      }, 400);
      setSearchDebounceTimer(timer);
    },
    [searchDebounceTimer, dispatch]
  );

  const handleSeverityFilterChange = useCallback(
    (value: string) => {
      setSeverityFilter(value);
      dispatch(ticketingActions.setPaginationParams({ page: 1 }));
    },
    [dispatch]
  );

  const handleTeamFilterChange = useCallback(
    (value: string) => {
      setTeamFilter(value);
      dispatch(ticketingActions.setPaginationParams({ page: 1 }));
    },
    [dispatch]
  );

  const handleApplyFilterPanel = useCallback(
    (values: FilterPanelValues) => {
      if (values.severity !== severityFilter) {
        setSeverityFilter(values.severity);
      }
      if (values.team !== teamFilter) {
        setTeamFilter(values.team);
      }
      setDateFromFilter(values.dateFrom);
      setDateToFilter(values.dateTo);
      setCategoryFilter(values.category);
      setSubCategoryFilter(values.subCategory);
      setTagsFilter(values.tags);
      // Map sort option to sortKey + sortDir
      switch (values.sortOrder) {
        case "oldest":
          _setSortKey("createdOn");
          _setSortDir("asc");
          break;
        case "titleAsc":
          _setSortKey("title");
          _setSortDir("asc");
          break;
        case "titleDesc":
          _setSortKey("title");
          _setSortDir("desc");
          break;
        case "ticketNumAsc":
          _setSortKey("ticketNumber");
          _setSortDir("asc");
          break;
        case "ticketNumDesc":
          _setSortKey("ticketNumber");
          _setSortDir("desc");
          break;
        default:
          _setSortKey("createdOn");
          _setSortDir("desc");
          break;
      }
      dispatch(ticketingActions.setPaginationParams({ page: 1 }));
    },
    [severityFilter, teamFilter, dispatch]
  );

  const handleOpenSubmit = useCallback(() => {
    const url = OnboardingUrls[RouteNames.TicketingSubmit].replace(
      ":sellerId",
      selectedAccessId || ""
    );
    history.push(url);
  }, [history, selectedAccessId]);
  const handleCloseEmailPrompt = useCallback(() => setIsEmailPromptOpen(false), []);
  const handleOpenSettings = useCallback(() => {
    const url = selectedAccessId
      ? `${OnboardingUrls[RouteNames.TicketingSettings]}?accessid=${selectedAccessId}`
      : OnboardingUrls[RouteNames.TicketingSettings];
    history.push(url);
  }, [history, selectedAccessId]);

  const handleAccessIdChange = useCallback(
    (newAccessId: string) => {
      dispatch(ticketingActions.setAccessId(newAccessId));
      // Sync to URL
      const params = new URLSearchParams(window.location.search);
      params.set("accessid", newAccessId);
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    },
    [dispatch]
  );

  const publisherOptions = useMemo(
    () =>
      sellers.map((s: PartnerCenterSeller) => ({
        id: s.id,
        label: s.name,
      })),
    [sellers]
  );

  const handleTicketClick = useCallback(
    (ticketId: string, ticketNumber?: string) => {
      const url = OnboardingUrls[RouteNames.TicketingTicketDetail]
        .replace(":sellerId", selectedAccessId || "")
        .replace(":ticketNumber", ticketNumber || ticketId);
      history.push(url);
    },
    [history, selectedAccessId]
  );

  useEffect(() => {
    if (!isSignedIn) {
      redirectToSignIn();
      return;
    }

    dispatch(ticketingActions.verifyAccess.start());
  }, [isSignedIn, dispatch]);

  /**
   * Unified fetch effect — fires whenever any fetch-relevant param changes.
   * Uses a ref to track whether counts should be included (first load + submit + seller switch).
   */
  const needsCountsRef = React.useRef(true);

  // Track ticket list height so the spinner preserves layout height during pagination
  const ticketListRef = useRef<HTMLDivElement>(null);
  const [tbodyMinHeight, setTbodyMinHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!isPaginatedFetching && ticketListRef.current && ticketListRef.current.offsetHeight > 0) {
      setTbodyMinHeight(ticketListRef.current.offsetHeight);
    }
  }, [isPaginatedFetching, tickets]);

  // Refetch counts when the selected seller changes
  useEffect(() => {
    needsCountsRef.current = true;
  }, [selectedAccessId]);

  // Load categories/subcategories for the filter panel
  useEffect(() => {
    if (isVerified && selectedAccessId) {
      dispatch(ticketingActions.fetchOptions.start(selectedAccessId));
    }
  }, [isVerified, selectedAccessId, dispatch]);

  useEffect(() => {
    if (!isVerified || !selectedAccessId) {
      return;
    }

    const crmSortField = sortKeyToCrmField[sortKey] ?? "createdon";
    const filterTypes: number[] = [];
    const filterSeverities = severityFilter
      ? [severityLabelToValue[severityFilter]].filter(Boolean)
      : [];
    const filterStatuses = statusFilter ? statusLabelToValues[statusFilter] ?? [] : [];

    const params: TicketPaginationParams = {
      accessId: selectedAccessId,
      page: pagination.currentPage,
      pageSize: pagination.pageSize,
      sortBy: crmSortField,
      sortDir,
      filterTypes,
      filterSeverities,
      filterStatuses,
      filterTeamId: teamFilter || undefined,
      filterDateFrom: dateFromFilter || undefined,
      filterDateTo: dateToFilter || undefined,
      filterCategoryId: categoryFilter || undefined,
      filterSubCategoryId: subCategoryFilter || undefined,
      filterTags: tagsFilter || undefined,
      search: searchQuery,
      queryToken: pagination.queryTokens[pagination.currentPage],
      includeCounts: needsCountsRef.current,
    };
    needsCountsRef.current = false;
    dispatch(ticketingActions.fetchPaginatedTickets.start(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isVerified,
    selectedAccessId,
    sortKey,
    sortDir,
    severityFilter,
    statusFilter,
    teamFilter,
    dateFromFilter,
    dateToFilter,
    categoryFilter,
    subCategoryFilter,
    tagsFilter,
    searchQuery,
    pagination.currentPage,
    pagination.pageSize,
    dispatch,
  ]);

  // Refetch tickets (with counts) after a successful submission
  useEffect(() => {
    if (submitSuccess) {
      needsCountsRef.current = true;
      // Reset filters and page to show new ticket
      setSeverityFilter("");
      setTeamFilter("");
      setStatusFilter("");
      setSearchQuery("");
      dispatch(ticketingActions.setPaginationParams({ page: 1 }));
    }
  }, [submitSuccess, dispatch]);

  // Keep accessid in URL whenever it changes
  useEffect(() => {
    if (!selectedAccessId) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("accessid") !== selectedAccessId) {
      params.set("accessid", selectedAccessId);
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    }
  }, [selectedAccessId]);

  // Deep link: auto-open ticket from ?ticketnumber= query param
  useEffect(() => {
    if (!isTicketsFetched || tickets.length === 0) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const ticketNumber = params.get("ticketnumber");
    if (!ticketNumber) {
      return;
    }
    const match = tickets.find(t => t.ticketNumber === ticketNumber);
    if (match) {
      handleTicketClick(match.id, match.ticketNumber);
    }
  }, [isTicketsFetched, tickets, handleTicketClick]);

  useEffect(() => {
    if (isNewContact) {
      setIsEmailPromptOpen(true);
    }
  }, [isNewContact]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("email-verified") === "true") {
      setIsEmailVerifiedSuccess(true);
      // Remove the query param from the URL without reload
      history.replace(location.pathname);
    }
  }, [location.search, history, location.pathname]);

  const counts = useMemo(
    () =>
      statusCountsFromRedux ?? {
        total: 0,
        proposed: 0,
        active: 0,
        resolved: 0,
        closed: 0,
      },
    [statusCountsFromRedux]
  );

  const statusTabFilters = useMemo(
    () => [
      "",
      TicketStatusLabel.Proposed,
      TicketStatusLabel.Active,
      TicketStatusLabel.Resolved,
      TicketStatusLabel.Closed,
    ],
    []
  );

  const statusTabItems = useMemo(() => {
    const countLabel = (name: string, count: number) => (count > 0 ? `${name} (${count})` : name);
    return [
      { label: countLabel(strings.ticketTotalLabel, counts.total) },
      { label: countLabel(strings.ticketProposedLabel, counts.proposed) },
      { label: countLabel(strings.ticketActiveLabel, counts.active) },
      { label: countLabel(strings.ticketResolvedLabel, counts.resolved) },
      { label: strings.ticketClosedLabel },
    ];
  }, [counts, strings]);

  const statusTabIndex = statusTabFilters.indexOf(statusFilter);

  const handleTabChange = useCallback(
    (index: number) => {
      const newFilter = statusTabFilters[index] ?? "";
      if (newFilter === statusFilter) {
        return;
      }
      setStatusFilter(newFilter);
      dispatch(ticketingActions.setPaginationParams({ page: 1 }));
    },
    [statusFilter, statusTabFilters, dispatch]
  );

  if (!isSignedIn) {
    return null;
  }

  if (!isVerifyChecked) {
    return (
      <>
        <div className={styles.outerContainer} style={{ minHeight: minPageHeight }}>
          <div
            className={styles.loadingContainer}
            role="status"
            aria-label={strings.ticketLoadingTicketsLabel}
          >
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  if (!isVerified) {
    return (
      <AccessDeniedPage
        footNote={strings.ticketAccessDeniedMsg}
        pageType={PageType.IdXboxApplication}
      />
    );
  }

  if (!isTicketsFetched) {
    return (
      <>
        <div className={styles.outerContainer} style={{ minHeight: minPageHeight }}>
          <div
            className={styles.loadingContainer}
            role="status"
            aria-label={strings.ticketLoadingTicketsLabel}
          >
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.outerContainer} style={{ minHeight: minPageHeight }}>
        <div className={styles.container}>
          <div className={styles.titleRow}>
            <div>
              <h1 className={styles.pageTitle}>{strings.ticketPageTitle}</h1>
              <p className={styles.pageSubtitle}>{strings.ticketPageSubtitle}</p>
            </div>
            <div className={styles.titleActions}>
              {accessMode === TicketingAccessMode.Seller && publisherOptions.length === 1 && (
                <span className={styles.publisherLabel}>{publisherOptions[0].label}</span>
              )}
              {accessMode === TicketingAccessMode.Seller && publisherOptions.length > 1 && (
                <SelectDropdown
                  className={styles.filterSelect}
                  value={selectedAccessId ?? ""}
                  onChange={handleAccessIdChange}
                  options={publisherOptions.map(opt => ({ id: opt.id, label: opt.label }))}
                  aria-label={strings.ticketPublisherDropdownLabel}
                />
              )}
              <HeroButton as="button" onClick={handleOpenSubmit}>
                {strings.ticketSubmitRequestLabel}
              </HeroButton>
            </div>
          </div>

          {/* Status Tabs */}
          <InlineTabs
            tabs={statusTabItems}
            value={statusTabIndex >= 0 ? statusTabIndex : 0}
            onValueChange={handleTabChange}
            className={styles.statusTabs}
            tabLabelClassName={styles.statusTabLabel}
            selectedTabLabelClassName={styles.statusTabLabelSelected}
            aria-label="Filter tickets by status"
          />

          {/* Filters */}
          <div className={styles.filtersRow}>
            <div className={styles.searchWrapper}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder={strings.ticketSearchPlaceholder}
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                aria-label={strings.ticketSearchAriaLabel}
              />
              {searchQuery && (
                <button
                  className={styles.searchClear}
                  onClick={() => handleSearchChange("")}
                  aria-label="Clear search"
                  type="button"
                >
                  ✕
                </button>
              )}
            </div>
            <SelectDropdown
              className={cx(styles.filterSelect, styles.dashboardFilter)}
              value={severityFilter}
              onChange={handleSeverityFilterChange}
              options={[
                { id: "", label: strings.ticketAllSeveritiesLabel },
                ...severityLevels.map(s => ({
                  id: s,
                  label: translateSeverityLabel(translationMap, s),
                })),
              ]}
              aria-label={strings.ticketAllSeveritiesLabel}
            />
            <SelectDropdown
              className={cx(styles.filterSelect, styles.dashboardFilter)}
              value={teamFilter}
              onChange={handleTeamFilterChange}
              options={[
                { id: "", label: strings.ticketAllTeamsLabel },
                { id: "private", label: strings.ticketTeamPrivateOption },
                ...teams.map(t => ({ id: t.id, label: t.name })),
              ]}
              aria-label={strings.ticketAllTeamsLabel}
            />
            <TicketingFilterPanel
              severityLevels={severityLevels}
              teams={teams}
              categories={categories}
              subCategories={subCategories}
              translationMap={translationMap}
              currentSeverityFilter={severityFilter}
              currentTeamFilter={teamFilter}
              strings={strings}
              onApply={handleApplyFilterPanel}
            />
          </div>

          {/* Ticket Cards */}
          <div className={styles.ticketCardList} role="group" aria-label="Tickets">
            {isPaginatedFetching ? (
              <div
                className={styles.ticketCardListLoading}
                role="status"
                aria-label={strings.ticketLoadingTicketsLabel}
                style={
                  tbodyMinHeight
                    ? { minHeight: `${Math.min(tbodyMinHeight, window.innerHeight * 0.6)}px` }
                    : undefined
                }
              >
                <LoadingSpinner />
              </div>
            ) : tickets.length === 0 ? (
              <div className={styles.noTicketsWrapper}>
                <img
                  src={getImage("ticketing_empty_search", ImageType.Png)}
                  alt=""
                  className={styles.noTicketsImage}
                />
                <p className={styles.noTicketsText}>{strings.ticketNoTicketsMsg}</p>
              </div>
            ) : (
              <div ref={ticketListRef} className={styles.ticketCardList}>
                {tickets.map(ticket => (
                  <div
                    key={ticket.id}
                    className={cx(styles.ticketCard, {
                      [styles.ticketCardProposed]: ticket.status === TicketStatusLabel.Proposed,
                      [styles.ticketCardActive]: ticket.status === TicketStatusLabel.Active,
                      [styles.ticketCardResolved]: ticket.status === TicketStatusLabel.Resolved,
                      [styles.ticketCardClosed]: ticket.status === TicketStatusLabel.Closed,
                    })}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleTicketClick(ticket.id, ticket.ticketNumber)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleTicketClick(ticket.id, ticket.ticketNumber);
                      }
                    }}
                    aria-label={strings.ticketRowAriaLabel
                      .replace("{0}", ticket.ticketNumber ?? "")
                      .replace("{1}", ticket.title)}
                  >
                    <div className={styles.ticketCardContent}>
                      <div className={styles.ticketCardMeta}>
                        {ticket.ticketNumber}
                        {" · "}
                        {ticket.teamName || strings.ticketTeamPrivateOption}
                        {ticket.category && (
                          <>
                            {" · "}
                            {ticket.category}
                          </>
                        )}
                        {ticket.subCategory && (
                          <>
                            {" · "}
                            {ticket.subCategory}
                          </>
                        )}
                      </div>
                      <div className={styles.ticketCardTitle}>{ticket.title}</div>
                      <div className={styles.ticketCardBadges}>
                        <span
                          className={cx(styles.ticketCardStatusBadge, {
                            [styles.statusProposed]: ticket.status === TicketStatusLabel.Proposed,
                            [styles.statusActive]: ticket.status === TicketStatusLabel.Active,
                            [styles.statusResolved]: ticket.status === TicketStatusLabel.Resolved,
                            [styles.statusClosed]: ticket.status === TicketStatusLabel.Closed,
                          })}
                        >
                          <Badge
                            icons={[
                              ticket.status === TicketStatusLabel.Proposed
                                ? Clock
                                : ticket.status === TicketStatusLabel.Active
                                ? StatusExclamation
                                : ticket.status === TicketStatusLabel.Resolved
                                ? CheckInCircle
                                : CircleStop,
                            ]}
                            iconSize={IconSize.XXSmall}
                            className={styles.ticketCardStatusIcon}
                          />
                          {translateStatusLabel(translationMap, ticket.status)}
                        </span>
                        <span
                          className={cx(styles.ticketCardSeverityBadge, {
                            [styles.severityCritical]:
                              ticket.severity === TicketSeverityLabel.Critical,
                          })}
                        >
                          {translateSeverityLabel(translationMap, ticket.severity)}
                        </span>
                        {ticket.submittedBy && (
                          <>
                            <span className={styles.ticketCardDot}>·</span>
                            <span className={styles.ticketCardCreatedBy}>
                              {strings.ticketCardCreatedBy
                                .replace("{0}", ticket.submittedBy)
                                .replace("{1}", ticket.createdOn ?? "—")}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={styles.ticketCardChevron} aria-hidden="true">
                      ›
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {pagination.totalPages > 1 && !isPaginatedFetching && (
            <div className={styles.paginationRow}>
              <HeroButton
                as="button"
                buttonType={ButtonType.Secondary}
                className={styles.paginationButton}
                disabled={pagination.currentPage <= 1 || isPaginatedFetching}
                onClick={() => {
                  dispatch(
                    ticketingActions.setPaginationParams({ page: pagination.currentPage - 1 })
                  );
                  window.scrollTo(0, 0);
                }}
                aria-label={strings.ticketPaginationPrevAriaLabel}
              >
                <span className={styles.paginationArrow}>‹</span> {strings.ticketPaginationPrev}
              </HeroButton>
              <span className={styles.paginationInfo}>
                {strings.ticketPaginationInfo
                  .replace("{0}", String(pagination.currentPage))
                  .replace("{1}", String(pagination.totalPages))}
              </span>
              <HeroButton
                as="button"
                buttonType={ButtonType.Secondary}
                className={styles.paginationButton}
                disabled={pagination.currentPage >= pagination.totalPages || isPaginatedFetching}
                onClick={() => {
                  dispatch(
                    ticketingActions.setPaginationParams({ page: pagination.currentPage + 1 })
                  );
                  window.scrollTo(0, 0);
                }}
                aria-label={strings.ticketPaginationNextAriaLabel}
              >
                {strings.ticketPaginationNext} <span className={styles.paginationArrow}>›</span>
              </HeroButton>
            </div>
          )}
          <div className={styles.notificationRow}>
            <div className={styles.notificationSectionWrapper}>
              <NotificationEmailSection />
            </div>
            <button
              className={styles.settingsButton}
              onClick={handleOpenSettings}
              aria-label={strings.ticketSettingsAriaLabel}
              title={strings.ticketSettingsTitle}
            >
              <Icon icon={Settings} aria-hidden={true} iconSize={IconSize.Custom} />
              {strings.ticketSettingsLabel}
            </button>
          </div>
        </div>
        <NotificationEmailPrompt isOpen={isEmailPromptOpen} onClose={handleCloseEmailPrompt} />
        <Modal
          isOpen={isEmailVerifiedSuccess}
          onClose={() => setIsEmailVerifiedSuccess(false)}
          width={420}
          ariaLabelledBy="email-verified-success"
        >
          <div className={styles.verifiedSuccessContainer}>
            <h2 id="email-verified-success" className={styles.verifiedSuccessTitle}>
              {strings.ticketEmailVerifiedTitle}
            </h2>
            <p className={styles.verifiedSuccessMessage}>{strings.ticketEmailVerifiedMsg}</p>
            <button
              className={styles.verifiedSuccessButton}
              onClick={() => setIsEmailVerifiedSuccess(false)}
            >
              {strings.ticketOkLabel}
            </button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default TicketingPage;
